// M14 — decor layer. Renders decoration sprites (trees, rocks, bushes,
// sand patches, etc.) that are placed per-tile in zones and streamed
// alongside terrain via the chunkData payload (see protocol.ts).
//
// Architecture mirrors ChunkedTilemap: one Container per loaded chunk,
// added to / removed from the layer's root container as chunks arrive
// or get evicted. Per-chunk sub-containers allow O(1) chunk clearing
// without scanning the whole layer.
//
// Anchor: every decor sprite anchors at (0.5, 1.0) — base at the
// bottom-center pixel of the texture — so the base sits on the iso
// diamond center of (tx, ty).
//
// Depth sort: per-chunk container uses `sortableChildren = true`
// with `sprite.zIndex = ty`, so within a single chunk trees north sort
// behind trees south. Cross-chunk and cross-layer sort (player ↔ tree)
// is a follow-up — for the MVP the whole DecorLayer sits BELOW the
// entity layer, so players always render in front of trees. Acceptable
// trade-off until we unify decor + entities under a single sortable
// container.

import { Assets, Container, Sprite, type Texture } from "pixi.js";
import {
  type ChunkKey,
  chunkKey,
  DECOR_KINDS,
  DECOR_SPRITES,
  type DecorPlacement,
} from "@vinyl/shared";
import { tileToScreen } from "./iso.js";

// Defensive: warn if a kind exists in the shared list but has no sprite
// mapping. Caught at module load (before any zone enter), so dev notices
// typos immediately. Doesn't throw — graceful degradation.
for (const kind of DECOR_KINDS) {
  if (!(kind in DECOR_SPRITES)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[decor] kind "${kind}" listed in DECOR_KINDS but no sprite path in DECOR_SPRITES`,
    );
  }
}

// Per-asset texture cache. Pixi's Assets.load already caches internally,
// but holding the resolved texture locally avoids the promise overhead
// every time we instantiate a sprite. Failed loads are remembered as
// null so we don't retry endlessly.
const textureCache = new Map<string, Texture | null>();

async function loadTexture(path: string): Promise<Texture | null> {
  if (textureCache.has(path)) return textureCache.get(path) ?? null;
  try {
    const tex = (await Assets.load(path)) as Texture;
    textureCache.set(path, tex);
    return tex;
  } catch {
    // Missing PNG / network 404 / decode failure — treat as "no sprite",
    // log once at console.warn so dev sees the issue but the game keeps
    // running (the placement just renders nothing).
    if (!textureCache.has(path)) {
      // eslint-disable-next-line no-console
      console.warn(`[decor] failed to load ${path}`);
    }
    textureCache.set(path, null);
    return null;
  }
}

export class DecorLayer {
  readonly container = new Container();
  private readonly chunkContainers = new Map<ChunkKey, Container>();

  constructor() {
    this.container.label = "decor";
  }

  // Drop every chunk's sprites. Called on zone change.
  clear(): void {
    this.container.removeChildren();
    for (const c of this.chunkContainers.values()) {
      c.destroy({ children: true });
    }
    this.chunkContainers.clear();
  }

  // Ingest the decor entries for a single chunk. Idempotent — re-ingesting
  // the same chunk replaces its previous sprites (mirrors ChunkedTilemap).
  // Sprite creation is async because textures load on demand; ordering
  // within the chunk's container is fixed once textures arrive.
  ingest(
    cx: number,
    cy: number,
    placements: readonly DecorPlacement[],
  ): void {
    const key = chunkKey(cx, cy);
    const old = this.chunkContainers.get(key);
    if (old) {
      this.container.removeChild(old);
      old.destroy({ children: true });
      this.chunkContainers.delete(key);
    }
    // Empty chunk — skip the container allocation entirely.
    if (placements.length === 0) return;

    const chunkContainer = new Container();
    chunkContainer.label = `decor:${key}`;
    chunkContainer.sortableChildren = true;
    this.chunkContainers.set(key, chunkContainer);
    this.container.addChild(chunkContainer);

    // Capture the key so a slow texture load racing against a clear() or
    // re-ingest doesn't add a sprite into a stale (destroyed) container.
    for (const p of placements) {
      const path = DECOR_SPRITES[p.kind];
      if (!path) continue; // unknown kind, drop silently
      void loadTexture(path).then((tex) => {
        if (!tex) return;
        const current = this.chunkContainers.get(key);
        if (current !== chunkContainer) return; // chunk replaced/cleared
        const sprite = new Sprite(tex);
        sprite.anchor.set(0.5, 1.0);
        const { sx, sy } = tileToScreen(p.tx, p.ty);
        sprite.position.set(sx, sy);
        sprite.zIndex = p.ty;
        sprite.label = `${p.kind}@${p.tx},${p.ty}`;
        chunkContainer.addChild(sprite);
      });
    }
  }

  destroy(): void {
    this.clear();
    this.container.destroy({ children: true });
  }
}
